import React from 'react';
import Form from "react-jsonschema-form";
import ImageFileWidget from "./rjsf/ImageFileWidget";
import { Col, Row } from 'react-bootstrap';
import Spinner from '../ui/Spinner'
import FormModelInspector from './FormModelInspector';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import * as ReactDOM from "react-dom";
import {FormattedMessage, injectIntl} from "react-intl";
import i18n from "../../main/i18n";
import LookingLocalAddressField from "./rjsf/LookingLocalAddressField";
import addressTypes from "../../config/addressTypes"
import taxonomyService from '../../main/taxonomyService';
import { withRouter } from 'react-router-dom';
import { logScanEvent } from "../UsageAnalytics";

class EvidenceForm extends React.Component {

  constructor(props) {
    super(props);

    this.errors = null;
    this.msg = i18n.getMsg(props.intl);

    this.transformErrors = errors => {

      this.state.errors = errors;
      return (errors || []).map(error => {
        let customMessage = props.validationMessages[error.property];

        if (customMessage) {
          // Try to use the custom message as a key and get the corresponding message from all.json. If no such key
          // exists, the message itself will be returned. Therefore, when specifying a custom message, either a key
          // or a message can be provided.
          customMessage = this.msg(customMessage, true);
        }
        error.message = customMessage || error.message;
        return error;
      });
    };

    // enable the form model inspector in the dev env
    this.isDebugMode = process.env.NODE_ENV === 'development';

    const {serviceAreaId, evidenceTypeId} = props.match.params;
    this.serviceArea = taxonomyService.getServiceArea(serviceAreaId);
    this.evidenceType = taxonomyService.getEvidenceType(this.serviceArea, evidenceTypeId);

    this.state = {
      errors: null,
      liveValidate: false,
      formData: {
        // workaround for this issue https://github.com/mozilla-services/react-jsonschema-form/issues/900
        images: []
      }
    };
  }

  componentDidMount() {
    if (this.serviceArea.allowNoAddress) this.props.intl.noAddressValue = this.serviceArea.allowNoAddress;
    else this.props.intl.noAddressValue = null;
    this.customFields = {
      [addressTypes.lookingLocal.fieldName]: LookingLocalAddressField
    }
  }

  hasErrors() {
    return this.state.errors && this.state.errors.length > 0;
  }

  errorCount() {
    return this.hasErrors() ? this.state.errors.length : 0;
  }

  onFormSubmitValidationFailure = errors => {
    if (errors.length) {
      console.debug('Validation errors', errors);
      this.setState({liveValidate: true});
      const formWrapper = ReactDOM.findDOMNode(this.refs.formWrapper);
      window.scrollTo(0, formWrapper.offsetTop);
    }
  };

  /**
   * The only reason we listen to form data changes is so that we can show the current model in the FormModelInspector,
   * which is just a debugging aid. In other words, if this handler (and the FormModelInspector) were removed, the
   * correct data would still be passed to the form submission handler.
   *
   * @param formData the current form model
   */
  handleFormDataChange = ({formData}) => {
    // workaround for this issue https://github.com/mozilla-services/react-jsonschema-form/issues/900
    if (!formData.images) {
      formData.images = [];
    }

    // log scan event when images are added;
    if (this.state.formData.images.length !== formData.images.length) {
      const pageCount = formData.images.length || 0;
      logScanEvent(this.serviceArea, this.evidenceType, pageCount, "EvidenceForm");
    }

    this.setState({formData});
  };


  renderErrorsAlert() {

    if (this.hasErrors()) {

      const errorMessage = this.errorCount() === 1 ?
        <FormattedMessage
          id="fixError"
          defaultMessage="Please fix the error before trying to submit this form again"/> :
        <FormattedMessage
          id="fixErrors"
          defaultMessage="Please fix all errors before trying to submit this form again"/>;

      return <p className='alert alert-warning'>{errorMessage}</p>
    }
  }

  renderModelInspector() {
    if (this.isDebugMode) {
      return (
        <Col md={6}>
          <FormModelInspector formData={this.state.formData}/>
        </Col>
      )
    }
  }

  render() {
    const formClasses = classNames("json-schema-form", {hidden: this.props.submissionInProgress});

    const evidenceGuidanceList = this.props.evidenceGuidance && (
      <ul key='guidance-list'>
        {
          this.props.evidenceGuidance.list.map((item, index) => <li key={index}>{item}</li>)
        }
      </ul>
    );

    return (
      <Row>
        <Col md={6} ref="formWrapper">
          <Spinner loading={this.props.submissionInProgress}/>

          {
            this.props.introText &&
            <div className='alert alert-info'>{this.props.introText}</div>
          }

          {this.renderErrorsAlert()}

          <Form id="evidence"
                schema={this.props.schema}
                uiSchema={this.props.uiSchema}
                formData={this.state.formData}
                transformErrors={this.transformErrors}
                validate={this.props.validate}
                liveValidate={this.state.liveValidate}
                onChange={this.handleFormDataChange}
                noHtml5Validate={true}
                className={formClasses}
                onError={this.onFormSubmitValidationFailure}
                onSubmit={this.props.onSubmit}
                showErrorList={false}
                widgets={this.props.widgets}
                fields={this.customFields}>

            {
              this.props.evidenceGuidance &&
              <div className="alert alert-info evidence-guidance">
                <p key='guidance-intro'>{this.props.evidenceGuidance.title}</p>
                {evidenceGuidanceList}
              </div>
            }

            {this.renderErrorsAlert()}

            <footer className="form-actions">
              <button type="submit" form="evidence" className="btn btn-primary btn-lg btn-fill"
                      disabled={this.hasErrors()}>
                <FormattedMessage id="submit" defaultMessage="Submit"/>
              </button>
            </footer>

          </Form>
        </Col>

        {this.renderModelInspector()}
      </Row>
    );
  }
}

export default withRouter(injectIntl(EvidenceForm));

EvidenceForm.propTypes = {
  schema: PropTypes.object.isRequired,
  uiSchema: PropTypes.object.isRequired,
  validate: PropTypes.func.isRequired,
  validationMessages: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  widgets: PropTypes.object.isRequired,
  submissionInProgress: PropTypes.bool.isRequired,
  introText: PropTypes.string,
  evidenceGuidance: PropTypes.object
};

EvidenceForm.defaultProps = {
  validate: (formData, errors) => errors,
  validationMessages: {},
  widgets: {FileWidget: ImageFileWidget},
  submissionInProgress: false
};
